import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrgUnitPicker from '@shared/components/OrgUnitPicker';
import OrgUnitSearchField from '@shared/components/OrgUnitSearchField';
import PositionPicker from '@shared/components/PositionPicker';
import PositionSearchField from '@shared/components/PositionSearchField';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import {
    HR_REPORT_SECTIONS,
    isSectionVisible,
} from '../constants/hrInformationReports';

function HrInformationSection({ title, children }) {
    return (
        <div className="hr-information-section">
            {title ? <span className="hr-information-section__title">{title}</span> : null}
            {children}
        </div>
    );
}

export default function HrInformationBody({ selectedReport = '', onFormStateChange }) {
    const { t } = useTranslation();
    const [showOrgUnitPicker, setShowOrgUnitPicker] = useState(false);
    const [selectedOrgUnitLabel, setSelectedOrgUnitLabel] = useState('');
    const [selectedOrgUnitId, setSelectedOrgUnitId] = useState(null);
    const [showPositionPicker, setShowPositionPicker] = useState(false);
    const [selectedPositionLabel, setSelectedPositionLabel] = useState('');
    const [selectedPositionId, setSelectedPositionId] = useState(null);
    const [forDate, setForDate] = useState(() => new Date());
    const [fromDate, setFromDate] = useState(() => new Date());
    const [toDate, setToDate] = useState(() => new Date());
    const [lengthOfServiceStatus, setLengthOfServiceStatus] = useState('working');
    const [maleYears, setMaleYears] = useState('');
    const [maleMonths, setMaleMonths] = useState('');
    const [femaleYears, setFemaleYears] = useState('');
    const [femaleMonths, setFemaleMonths] = useState('');
    const [orderedBy, setOrderedBy] = useState('1');

    const show = (section) => isSectionVisible(selectedReport, section);

    useEffect(() => {
        setShowOrgUnitPicker(false);
        setShowPositionPicker(false);
    }, [selectedReport]);

    useEffect(() => {
        if (typeof onFormStateChange !== 'function') return;
        onFormStateChange({
            selectedReport,
            selectedOrgUnitId,
            selectedOrgUnitLabel,
            selectedPositionId,
            selectedPositionLabel,
            forDate,
            fromDate,
            toDate,
            lengthOfServiceStatus,
            maleYears,
            maleMonths,
            femaleYears,
            femaleMonths,
            orderedBy,
        });
    }, [
        onFormStateChange,
        selectedReport,
        selectedOrgUnitId,
        selectedOrgUnitLabel,
        selectedPositionId,
        selectedPositionLabel,
        forDate,
        fromDate,
        toDate,
        lengthOfServiceStatus,
        maleYears,
        maleMonths,
        femaleYears,
        femaleMonths,
        orderedBy,
    ]);

    const openOrgUnitPicker = useCallback(() => {
        setShowOrgUnitPicker(true);
    }, []);

    const handleOrgUnitPickerCancel = useCallback(() => {
        setShowOrgUnitPicker(false);
    }, []);

    const handleOrgUnitSelect = useCallback((orgUnitId, label) => {
        setSelectedOrgUnitId(orgUnitId);
        setSelectedOrgUnitLabel(label || '');
        setShowOrgUnitPicker(false);
    }, []);

    const openPositionPicker = useCallback(() => {
        setShowPositionPicker(true);
    }, []);

    const handlePositionPickerCancel = useCallback(() => {
        setShowPositionPicker(false);
    }, []);

    const handlePositionSelect = useCallback((positionId, label) => {
        setSelectedPositionId(positionId);
        setSelectedPositionLabel(label || '');
        setShowPositionPicker(false);
    }, []);

    if (!selectedReport) {
        return null;
    }

    return (
        <div className="flex-column gap-20 width-100">
            {show(HR_REPORT_SECTIONS.ORG_UNIT) && (
                <HrInformationSection title={t('users.table.search.options.structuralGroup')}>
                    <OrgUnitSearchField
                        displayLabel={selectedOrgUnitLabel}
                        onOpenPicker={openOrgUnitPicker}
                    />
                    {showOrgUnitPicker && (
                        <OrgUnitPicker
                            showSearch={false}
                            panelWidth="100%"
                            cancelLabelKey="common.exit"
                            onSelect={handleOrgUnitSelect}
                            onCancel={handleOrgUnitPickerCancel}
                        />
                    )}
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.POSITION) && (
                <HrInformationSection title={t('persons.personContract.selectionOfPosition')}>
                    <PositionSearchField
                        displayLabel={selectedPositionLabel}
                        onOpenPicker={openPositionPicker}
                    />
                    {showPositionPicker && (
                        <PositionPicker
                            showSearch={false}
                            panelWidth="100%"
                            cancelLabelKey="common.exit"
                            onSelect={handlePositionSelect}
                            onCancel={handlePositionPickerCancel}
                        />
                    )}
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.AGE) && (
                <HrInformationSection title={t('staff.hrInformation.age')}>
                    <div className="flex-row width-100 gap-10">
                        <div className="flex-column gap-10">
                            <div style={{ minHeight: '24px' }} aria-hidden="true" />
                            <div className="flex-column gap-10">
                                <span className="no-wrap">{t('persons.workExperience.years')}</span>
                                <span className="no-wrap">{t('persons.workExperience.months')}</span>
                            </div>
                        </div>
                        <div className="flex-column align-center gap-10">
                            <span className="span-bold no-wrap text-align-center">{t('staff.hrInformation.male')}</span>
                            <input
                                type="number"
                                className="small-input width-80px"
                                value={maleYears}
                                onChange={(e) => setMaleYears(e.target.value)}
                                min="0"
                            />
                            <input
                                type="number"
                                className="small-input width-80px"
                                value={maleMonths}
                                onChange={(e) => setMaleMonths(e.target.value)}
                                min="0"
                            />
                        </div>
                        <div className="flex-column align-center gap-10">
                            <span className="span-bold no-wrap text-align-center">{t('staff.hrInformation.female')}</span>
                            <input
                                type="number"
                                className="small-input width-80px"
                                value={femaleYears}
                                onChange={(e) => setFemaleYears(e.target.value)}
                                min="0"
                            />
                            <input
                                type="number"
                                className="small-input width-80px"
                                value={femaleMonths}
                                onChange={(e) => setFemaleMonths(e.target.value)}
                                min="0"
                            />
                        </div>
                    </div>
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.FOR_DATE) && (
                <HrInformationSection title={t('staff.hrInformation.fromDate')}>
                    <CustomDatePicker
                        name="forDate"
                        value={forDate}
                        onChange={setForDate}
                        isSmall={true}
                        placeholderText={t('staff.hrInformation.fromDate')}
                    />
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.BORN_TO) && (
                <HrInformationSection title={t('staff.hrInformation.bornTo')}>
                    <CustomDatePicker
                        name="bornToDate"
                        value={forDate}
                        onChange={setForDate}
                        isSmall={true}
                        placeholderText={t('staff.hrInformation.bornTo')}
                    />
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.PERIOD) && (
                <HrInformationSection title={t('staff.hrInformation.period')}>
                    <div className="flex-row gap-10 flex-wrap align-center">
                        <span className="no-wrap">{t('staff.hrInformation.fromDate')}</span>
                        <CustomDatePicker
                            name="fromDate"
                            value={fromDate}
                            onChange={setFromDate}
                            isSmall={true}
                            placeholderText={t('staff.hrInformation.fromDate')}
                        />
                        <span className="no-wrap">{t('staff.hrInformation.toDate')}</span>
                        <CustomDatePicker
                            name="toDate"
                            value={toDate}
                            onChange={setToDate}
                            isSmall={true}
                            placeholderText={t('staff.hrInformation.toDate')}
                        />
                    </div>
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.LENGTH_OF_SERVICE) && (
                <HrInformationSection title={t('staff.hrInformation.lengthOfService')}>
                    <label className="flex-row gap-10">
                        <input
                            type="radio"
                            name="lengthOfServiceStatus"
                            value="working"
                            checked={lengthOfServiceStatus === 'working'}
                            onChange={(e) => setLengthOfServiceStatus(e.target.value)}
                        />
                        <span className="span">{t('staff.hrInformation.working')}</span>
                    </label>
                    <label className="flex-row gap-10">
                        <input
                            type="radio"
                            name="lengthOfServiceStatus"
                            value="terminated"
                            checked={lengthOfServiceStatus === 'terminated'}
                            onChange={(e) => setLengthOfServiceStatus(e.target.value)}
                        />
                        <span className="span">{t('staff.hrInformation.terminated')}</span>
                    </label>
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.ADDITIONAL_COLUMNS) && (
                <HrInformationSection title={t('staff.hrInformation.additionalColumns')}>
                    <label className="flex-row gap-10">
                        <input type="checkbox" />
                        <span className="span">{t('staff.hrInformation.basicMonthlySalaryAndLengthOfServiceAllowance')}</span>
                    </label>
                    <label className="flex-row gap-10">
                        <input type="checkbox" />
                        <span className="span">{t('staff.hrInformation.costCenter')}</span>
                    </label>
                </HrInformationSection>
            )}

            {show(HR_REPORT_SECTIONS.ORDERED_BY) && (
                <HrInformationSection title={t('staff.hrInformation.orderedBy')}>
                    <select
                        className="small-select width-50"
                        value={orderedBy}
                        onChange={(e) => setOrderedBy(e.target.value)}
                    >
                        <option value="1">{t('staff.hrInformation.name')}</option>
                        <option value="2">{t('staff.hrInformation.structuralGroup')}</option>
                    </select>
                </HrInformationSection>
            )}
        </div>
    );
}
